import {ApplicationResource, ApplicationsService} from "../../core/modules/openapi";
import {DataSource} from "@angular/cdk/collections";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {EventEmitter, inject, signal} from "@angular/core";
import {catchError, merge, Observable, of as observableOf, switchMap} from "rxjs";
import {map, startWith} from "rxjs/operators";
import Keycloak from "keycloak-js";
import {jwtDecode} from 'jwt-decode';
import {StatusEnum} from "../applications-criteria/applications-criteria.component";

export interface TableItem extends ApplicationResource {
}

export class ApplicationsTableDataSource extends DataSource<TableItem> {
  data: TableItem[] = [];
  paginator: MatPaginator | undefined;
  sort: MatSort | undefined;
  status: [string, StatusEnum][] = [["NEW", StatusEnum.NEW], ["IN_PROGRESS", StatusEnum.IN_PROGRESS], ["IN_ISSUING", StatusEnum.IN_ISSUING]];
  insurerId: string | undefined;
  applicantId: string | undefined;
  insurableInterestIdentifier: string | undefined;
  quotationIdentifier: string | undefined;
  policyIdentifier: string | undefined;
  certificateIdentifier: string | undefined;
  inspectionIdentifier: string | undefined;
  criteriaChanged: EventEmitter<any> = new EventEmitter();
  resultsLength = signal(0);
  isLoadingResults = signal(true);
  private readonly keycloak = inject(Keycloak);

  constructor(private readonly applicationsService: ApplicationsService) {
    super();
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<TableItem[]> {
    if (this.paginator && this.sort && this.keycloak.token && this.criteriaChanged) {
      // Combine everything that affects the rendered data into one update
      // stream for the data-table to consume.
      let claims = jwtDecode<any>(this.keycloak.token);
      let brokerId = claims["broker"];

      return merge(this.paginator.page, this.sort.sortChange, this.criteriaChanged).pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults.set(true);
          const sortStr = this.sort!.active + "," + this.sort!.direction;
          return this.applicationsService.getApplicationsByCriteria(brokerId, this.status?.map(value => value[0]),
            this.insurerId, this.applicantId, this.insurableInterestIdentifier, this.quotationIdentifier,
            this.policyIdentifier, this.certificateIdentifier, this.inspectionIdentifier, undefined,
            this.paginator!.pageIndex, ["lastModificationDatetime,desc", sortStr],
            this.paginator!.pageSize).pipe(
            catchError(() => observableOf({content: [], totalElements: 0})),
            map((data) => {
              // Flip flag to show that loading has finished.
              this.isLoadingResults.set(false);
              this.resultsLength.set(data.totalElements!);
              return data.content as TableItem[];
            })
          );
        })
      );
    } else {
      throw Error(
        'Please set the paginator, sort and database on the data source before connecting.'
      );
    }
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  disconnect(): void {
  }
}
